<template>
  <div class="tasks-page" :class="`tasks-page--${theme}`">
    <!-- Toolbar: the Lead team filter (limited to the user's own teams unless they are an
         administrator) and, while a visual's click selection is active, a chip to clear it -->
    <!-- Same layout and field style as the date range on the Activity tab -->
    <v-row class="toolbar">
      <v-col>
        <v-autocomplete
          v-model="taskSCurveFilters.teamIds"
          :items="teamOptions"
          item-title="name"
          item-value="id"
          :label="$t('taskSCurve.leadTeam')"
          multiple
          chips
          closable-chips
          clearable
          variant="solo"
          hide-details
        />
      </v-col>
    </v-row>

    <!-- Row 1: Overall + Current tasks -->
    <div class="quarters quarters--2 mb-4">
      <TaskOverallCard :overview="overview" :loading="loading || !dataLoaded || !isTaskScopeReady" />
      <TaskCurrentTasksCard :overview="bucketOverview" :loading="loading || !dataLoaded || !isTaskScopeReady" />
    </div>

    <!-- Row 2. Sync: Workload · Recent activity · Others waiting on us.
         Lite (no planned dates): Pace · Recent activity · Progress by drawing.
         The side cards wait for the schedule mode so the right pair is mounted once; the mode
         is kept even when a cross-filter leaves the overview empty, so a source visual never
         disappears while its own selection is active. -->
    <div class="quarters quarters--3 mb-4">
      <TaskWorkloadChart v-if="scheduleMode && !isLite" />
      <TaskPaceChart v-if="scheduleMode && isLite" />
      <TaskRecentActivityCard />
      <TaskWaitingOnUsCard v-if="scheduleMode && !isLite" />
      <TaskProgressByDrawingCard v-if="scheduleMode && isLite" />
    </div>

    <TaskSCurveLineChart />

    <!-- Task details: one row per leaf task in scope -->
    <TaskDetailsGrid />
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useStore } from 'vuex';
import TaskSCurveLineChart from './charts/TaskSCurveLineChart.vue';
import TaskWorkloadChart from './charts/TaskWorkloadChart.vue';
import TaskPaceChart from './charts/TaskPaceChart.vue';
import TaskOverallCard from './cards/TaskOverallCard.vue';
import TaskCurrentTasksCard from './cards/TaskCurrentTasksCard.vue';
import TaskRecentActivityCard from './cards/TaskRecentActivityCard.vue';
import TaskWaitingOnUsCard from './cards/TaskWaitingOnUsCard.vue';
import TaskProgressByDrawingCard from './cards/TaskProgressByDrawingCard.vue';
import TaskDetailsGrid from './grids/TaskDetailsGrid.vue';
import { sCurveSql, scheduleSummarySql, taskStatusSql, teamLockSql } from '@/utils/taskTables';
import {
  taskSCurveFilters,
  isTaskScopeReady,
  isTaskScopeRestricted,
  taskScope,
  taskScopeKey,
  setLockedTeams,
} from '@/utils/taskSCurveFilters';
import { getCurrentUserTeamIds, toSqlIdList } from '@/utils/userTeams';
import { daysFromToday } from '@/utils/taskDates';

const store = useStore();
const conn = computed(() => store.getters.getDBConnection);
const dataLoaded = computed(() => store.getters.getDataLoaded);
const userId = computed(() => store.getters.getUserId);
const theme = computed(() => (store.state.theme === 'light' ? 'light' : 'dark'));

// One overview object feeds both top cards: status counts, schedule span and today's curve %.
// Same scope as every other visual on the tab (taskSCurveFilters).
const overview = ref(null);
// Same counts with the status cross-filter ignored: the Current tasks card must keep showing
// every bucket so the selection can be changed
const bucketOverview = ref(null);
const loading = ref(false);
// 'sync' | 'lite', known from the first status query and kept from then on
const scheduleMode = ref(null);
const isLite = computed(() => scheduleMode.value === 'lite');

// Lead team filter options: teams leading at least one sub-task, within the permission scope
const teamOptions = ref([]);

let queryVersion = 0;

const queryRows = async (query) => {
  const result = await conn.value.query(query);
  // COUNT(*) etc. come back as BigInt, so numeric columns are wrapped in Number() below
  return result.toArray().map(row => row.toJSON());
};

const num = v => (v === null || v === undefined ? null : Number(v));
const count = v => num(v) ?? 0;

// Teams whose code marks them as administrators: RDrive Administrators (ADMIN) and
// Project Administrators (PROJADMIN). Members of either see the whole schedule.
const ADMIN_TEAM_CODES = ['ADMIN', 'PROJADMIN'];
const isAdminTeam = (team) => {
  // The code column's exact name isn't pinned down, so prefer a column called Code and
  // otherwise accept the code anywhere in the row
  const codeKey = Object.keys(team).find(k => k.toLowerCase() === 'code');
  const values = codeKey ? [team[codeKey]] : Object.values(team);
  return values.some(v => typeof v === 'string' && ADMIN_TEAM_CODES.includes(v.toUpperCase()));
};

// true = administrator, so the tab is not locked to the user's teams
const isAdmin = ref(false);

// The tab is permission aware: a user only sees tasks whose sub-tasks are led by one of their
// teams, unless one of those teams is an administrators team. Resolving this sets
// taskSCurveFilters.lockedTeamIds, which every query reads.
const resolveUserTeams = async () => {
  if (!dataLoaded.value) return;
  try {
    const ids = await getCurrentUserTeamIds(conn.value, userId.value);
    const teams = ids.length
      ? await queryRows(`SELECT * FROM teams WHERE Id IN (${toSqlIdList(ids)})`)
      : [];
    isAdmin.value = teams.some(isAdminTeam);
    setLockedTeams(isAdmin.value ? null : ids);
  } catch (error) {
    console.error('[TasksPage] failed to resolve the user\'s teams:', error.message);
    isAdmin.value = false;
    setLockedTeams([]);
  }
  await loadTeamOptions();
};

const loadTeamOptions = async () => {
  try {
    teamOptions.value = await queryRows(`
      SELECT u.leadByTeamId AS id, COALESCE(t.Name, u.leadByTeamId) AS name
      FROM (SELECT DISTINCT leadByTeamId FROM s_curve_units
            WHERE leadByTeamId IS NOT NULL AND ${teamLockSql(taskSCurveFilters.lockedTeamIds, 'leadByTeamId')}) u
      LEFT JOIN teams t ON t.Id = u.leadByTeamId
      ORDER BY name
    `);
  } catch (error) {
    console.error('[TasksPage] failed to load the team options:', error.message);
    teamOptions.value = [];
  }
};

const loadOverview = async () => {
  if (!dataLoaded.value || !isTaskScopeReady.value) return;
  const version = ++queryVersion;
  loading.value = true;

  const scope = taskScope();
  // Unrestricted: the table built at load time. Otherwise the same SQL with a WHERE on s_curve_units.
  const curveSql = isTaskScopeRestricted.value ? sCurveSql(scope) : `SELECT * FROM s_curve`;

  try {
    const statusFiltered = taskSCurveFilters.statuses.length > 0;
    const [[status], [schedule], [today], [bucketStatus]] = await Promise.all([
      queryRows(taskStatusSql(scope)),
      queryRows(scheduleSummarySql(scope)),
      // The curve's value today: the latest date on or before today, or the first date
      // if the schedule hasn't started yet (both curves are 0% there).
      queryRows(`
        SELECT targetProgressPct, actualProgressPct
        FROM (${curveSql}) q
        ORDER BY (date <= CURRENT_DATE) DESC,
                 CASE WHEN date <= CURRENT_DATE THEN date END DESC,
                 date
        LIMIT 1
      `),
      statusFiltered ? queryRows(taskStatusSql(taskScope(['status']))) : Promise.resolve([null]),
    ]);
    if (version !== queryVersion) return; // a newer request superseded this one

    if (status?.scheduleMode) scheduleMode.value = status.scheduleMode;

    const buildOverview = (st) => ({
      mode: st.scheduleMode ?? 'sync',
      total: count(st.total),
      open: count(st.open),
      completed: count(st.completed),
      upcoming: count(st.upcoming),
      onSchedule: count(st.onSchedule),
      behindSchedule: count(st.behindSchedule),
      overdue: count(st.overdue),
      overdueMaxDays: num(st.overdueMaxDays),
      notStarted: count(st.notStarted),
      inProgress: count(st.inProgress),
      stalled: count(st.stalled),
      completedThisWeek: count(st.completedThisWeek),
      completedLastWeek: count(st.completedLastWeek),
    });
    // With a status selected the buckets show the unfiltered picture; otherwise they follow Overall
    bucketOverview.value = statusFiltered
      ? (bucketStatus && count(bucketStatus.total) > 0 ? buildOverview(bucketStatus) : null)
      : (status && count(status.total) > 0 ? buildOverview(status) : null);

    if (!status || count(status.total) === 0) {
      overview.value = null;
      return;
    }

    const mode = status.scheduleMode ?? 'sync';
    // PM lite has no planned curve, so no planned % and no variance
    const planned = mode === 'lite' ? null : (num(today?.targetProgressPct) ?? 0);
    const actual = num(today?.actualProgressPct) ?? 0;

    overview.value = {
      ...buildOverview(status),
      mode,
      actual,
      planned,
      variance: planned === null ? null : actual - planned,
      plannedStartDate: schedule?.plannedStartDate ?? null,
      plannedEndDate: schedule?.plannedEndDate ?? null,
      // Negative once the planned end has passed (the card shows it as "Overrun n")
      daysLeft: daysFromToday(schedule?.plannedEndDate),
      daysBehind: num(schedule?.daysBehind),
    };
  } catch (error) {
    if (version !== queryVersion) return;
    console.error('[TasksPage] failed to load the task overview:', error.message);
    overview.value = null;
    bucketOverview.value = null;
  } finally {
    if (version === queryVersion) loading.value = false;
  }
};

onMounted(async () => {
  await store.commit('resetFilters');
  await resolveUserTeams();
  await loadOverview();
});

// The lock is resolved once the data is in; the overview follows the lock and the selections
watch(dataLoaded, resolveUserTeams);
watch([dataLoaded, taskScopeKey], loadOverview);
</script>

<style>
/* Theme tokens shared by the Tasks tab cards (global so the child cards inherit them).
   Light: darker tints for contrast on white. Dark: the Work Progress design's tints. */
.tasks-page--light {
  --tk-ok: #2E7D32;
  --tk-info: #1565C0;
  --tk-warn: #E65100;
  --tk-err: #C62828;
  --tk-bar-ok: #4CAF50;
  --tk-track: rgba(0, 0, 0, 0.10);
  --tk-divider: rgba(0, 0, 0, 0.10);
  --tk-row-hover: rgba(0, 0, 0, 0.03);
}
.tasks-page--dark {
  --tk-ok: #8BE09A;
  --tk-info: #7CC4FF;
  --tk-warn: #FFC266;
  --tk-err: #FF8A80;
  --tk-bar-ok: #4CAF50;
  --tk-track: rgba(255, 255, 255, 0.12);
  --tk-divider: rgba(255, 255, 255, 0.08);
  --tk-row-hover: rgba(255, 255, 255, 0.03);
}
.tasks-page .tk-ok { color: var(--tk-ok); }
.tasks-page .tk-info { color: var(--tk-info); }
.tasks-page .tk-warn { color: var(--tk-warn); }
.tasks-page .tk-err { color: var(--tk-err); }
</style>

<style scoped>
.toolbar {
  margin-bottom: 4px;
}
/* Cards side by side, stacking once a column would drop below its minimum width */
.quarters {
  display: grid;
  gap: 16px;
  align-items: stretch;
}
.quarters--2 {
  grid-template-columns: repeat(auto-fit, minmax(min(520px, 100%), 1fr));
}
.quarters--3 {
  grid-template-columns: repeat(auto-fit, minmax(min(380px, 100%), 1fr));
}
</style>
